import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { chequesApi } from '../../utils/chequesApi';
import { cashBanksApi } from '../../utils/cashBanksApi';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './CashBanks.css';

const fmt = (val) =>
  val != null
    ? Number(val).toLocaleString('en-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

const STATUS_LABELS = { ISSUED: 'Issued', CLEARED: 'Cleared', VOID: 'Void' };
const STATUS_BADGE  = { ISSUED: 'issued', CLEARED: 'cleared', VOID: 'void' };

// ── Issue Cheque Modal ────────────────────────────────────────────────
function IssueChequeModal({ accounts, onClose, onSave }) {
  const [form, setForm] = useState({
    accountId: '', bookId: '', payee: '', amount: '', amountWords: '',
    issueDate: new Date().toISOString().slice(0, 10), notes: '',
  });
  const [books, setBooks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (form.accountId) {
      chequesApi.listBooks({ accountId: form.accountId, isActive: 'true' })
        .then(setBooks).catch(() => setBooks([]));
    } else {
      setBooks([]);
    }
  }, [form.accountId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.accountId)  e.accountId  = 'Required';
    if (!form.bookId)     e.bookId     = 'Required';
    if (!form.payee.trim()) e.payee    = 'Required';
    if (!form.amount || Number(form.amount) <= 0) e.amount = 'Must be > 0';
    if (!form.issueDate)  e.issueDate  = 'Required';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const cheque = await chequesApi.issue({
        accountId:   form.accountId,
        bookId:      form.bookId,
        payee:       form.payee.trim(),
        amount:      Number(form.amount),
        amountWords: form.amountWords.trim() || undefined,
        issueDate:   form.issueDate,
        notes:       form.notes.trim() || undefined,
      });
      onSave(cheque);
    } catch (err) {
      setErrors({ api: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cb-modal-overlay">
      <div className="cb-modal">
        <div className="cb-modal-header">
          <h2 className="cb-modal-title">Issue New Cheque</h2>
          <button className="cb-btn cb-btn--icon" onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="cb-modal-body">
          {errors.api && <div className="cb-error" style={{ marginBottom: 12 }}><i className="fas fa-exclamation-circle" style={{ marginRight: 6 }} />{errors.api}</div>}

          <div className="cb-form-row">
            <label className="cb-label">Bank Account *</label>
            <select className="cb-select" value={form.accountId} onChange={(e) => { set('accountId', e.target.value); set('bookId', ''); }}>
              <option value="">Select account…</option>
              {accounts.filter(a => a.isBank).map(a => (
                <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
              ))}
            </select>
            {errors.accountId && <div className="cb-error">{errors.accountId}</div>}
          </div>

          <div className="cb-form-row">
            <label className="cb-label">Cheque Book *</label>
            <select className="cb-select" value={form.bookId} onChange={(e) => set('bookId', e.target.value)} disabled={!form.accountId}>
              <option value="">Select book…</option>
              {books.map(b => (
                <option key={b.id} value={b.id}>{b.bookCode} ({b.bankName || 'No bank name'})</option>
              ))}
            </select>
            {errors.bookId && <div className="cb-error">{errors.bookId}</div>}
            {form.accountId && books.length === 0 && (
              <div className="cb-hint">No active cheque book. Register one first.</div>
            )}
          </div>

          <div className="cb-form-row">
            <label className="cb-label">Payee *</label>
            <input className="cb-input" value={form.payee} onChange={(e) => set('payee', e.target.value)} placeholder="Pay to the order of…" />
            {errors.payee && <div className="cb-error">{errors.payee}</div>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="cb-form-row">
              <label className="cb-label">Amount *</label>
              <input className="cb-input" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="0.00" />
              {errors.amount && <div className="cb-error">{errors.amount}</div>}
            </div>
            <div className="cb-form-row">
              <label className="cb-label">Issue Date *</label>
              <input className="cb-input" type="date" value={form.issueDate} onChange={(e) => set('issueDate', e.target.value)} />
              {errors.issueDate && <div className="cb-error">{errors.issueDate}</div>}
            </div>
          </div>

          <div className="cb-form-row">
            <label className="cb-label">Amount in Words</label>
            <input className="cb-input" value={form.amountWords} onChange={(e) => set('amountWords', e.target.value)} placeholder="e.g. Five thousand Egyptian Pounds only" />
          </div>

          <div className="cb-form-row">
            <label className="cb-label">Notes</label>
            <input className="cb-input" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="cb-modal-footer">
          <button className="cb-btn cb-btn--secondary" onClick={onClose}>Cancel</button>
          <button className="cb-btn cb-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? <LoadingSpinner variant="button" /> : <><i className="fas fa-paper-plane" /> Issue Cheque</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Register Book Modal ───────────────────────────────────────────────
function RegisterBookModal({ accounts, onClose, onSave }) {
  const [form, setForm] = useState({ accountId: '', bookCode: '', bankName: '', startNumber: '', endNumber: '' });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.accountId) e.accountId = 'Required';
    if (!form.bookCode.trim()) e.bookCode = 'Required';
    if (!form.startNumber || Number(form.startNumber) < 1) e.startNumber = 'Must be ≥ 1';
    if (!form.endNumber || Number(form.endNumber) < Number(form.startNumber)) e.endNumber = 'Must be ≥ start';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const book = await chequesApi.createBook({
        accountId:   form.accountId,
        bookCode:    form.bookCode.trim(),
        bankName:    form.bankName.trim() || undefined,
        startNumber: Number(form.startNumber),
        endNumber:   Number(form.endNumber),
      });
      onSave(book);
    } catch (err) {
      setErrors({ api: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cb-modal-overlay">
      <div className="cb-modal">
        <div className="cb-modal-header">
          <h2 className="cb-modal-title">Register Cheque Book</h2>
          <button className="cb-btn cb-btn--icon" onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="cb-modal-body">
          {errors.api && <div className="cb-error" style={{ marginBottom: 12 }}>{errors.api}</div>}
          <div className="cb-form-row">
            <label className="cb-label">Bank Account *</label>
            <select className="cb-select" value={form.accountId} onChange={(e) => set('accountId', e.target.value)}>
              <option value="">Select account…</option>
              {accounts.filter(a => a.isBank).map(a => (
                <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
              ))}
            </select>
            {errors.accountId && <div className="cb-error">{errors.accountId}</div>}
          </div>
          <div className="cb-form-row">
            <label className="cb-label">Book Code *</label>
            <input className="cb-input" value={form.bookCode} onChange={(e) => set('bookCode', e.target.value)} placeholder="e.g. CIB-2026-001" />
            {errors.bookCode && <div className="cb-error">{errors.bookCode}</div>}
          </div>
          <div className="cb-form-row">
            <label className="cb-label">Bank Name</label>
            <input className="cb-input" value={form.bankName} onChange={(e) => set('bankName', e.target.value)} placeholder="e.g. CIB" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="cb-form-row">
              <label className="cb-label">Start Number *</label>
              <input className="cb-input" type="number" min="1" value={form.startNumber} onChange={(e) => set('startNumber', e.target.value)} />
              {errors.startNumber && <div className="cb-error">{errors.startNumber}</div>}
            </div>
            <div className="cb-form-row">
              <label className="cb-label">End Number *</label>
              <input className="cb-input" type="number" min="1" value={form.endNumber} onChange={(e) => set('endNumber', e.target.value)} />
              {errors.endNumber && <div className="cb-error">{errors.endNumber}</div>}
            </div>
          </div>
        </div>
        <div className="cb-modal-footer">
          <button className="cb-btn cb-btn--secondary" onClick={onClose}>Cancel</button>
          <button className="cb-btn cb-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? <LoadingSpinner variant="button" /> : <><i className="fas fa-plus" /> Register Book</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────
export default function ChequesPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [cheques, setCheques] = useState({ items: [], total: 0, page: 1, limit: 100 });
  const [filter, setFilter] = useState({ accountId: '', status: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [showIssue, setShowIssue] = useState(false);
  const [showBook, setShowBook] = useState(false);
  const [updateTarget, setUpdateTarget] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  useEffect(() => {
    cashBanksApi.listAccounts().then(setAccounts).catch(() => {});
  }, []);

  const loadCheques = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filter.accountId) params.accountId = filter.accountId;
      if (filter.status)    params.status    = filter.status;
      const data = await chequesApi.list(params);
      setCheques(data || { items: [], total: 0 });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadCheques(); }, [loadCheques]);

  const handleUpdate = async (id, status) => {
    try {
      await chequesApi.update(id, { status });
      showToast(`Cheque marked as ${status}`);
      loadCheques();
    } catch (e) {
      setError(e.message);
    }
    setUpdateTarget(null);
  };

  return (
    <div className="cb-page">
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, background: '#065f46', color: '#fff', padding: '10px 18px', borderRadius: 8, zIndex: 2000, fontWeight: 600 }}>
          <i className="fas fa-check-circle" style={{ marginRight: 8 }} />{toast}
        </div>
      )}

      {showIssue && (
        <IssueChequeModal
          accounts={accounts}
          onClose={() => setShowIssue(false)}
          onSave={(c) => { setShowIssue(false); showToast(`Cheque #${c.chequeNumber} issued`); loadCheques(); }}
        />
      )}
      {showBook && (
        <RegisterBookModal
          accounts={accounts}
          onClose={() => setShowBook(false)}
          onSave={() => { setShowBook(false); showToast('Cheque book registered'); }}
        />
      )}
      {updateTarget && (
        <div className="cb-modal-overlay">
          <div className="cb-modal">
            <div className="cb-modal-header">
              <h2 className="cb-modal-title">Update Cheque #{updateTarget.chequeNumber}</h2>
              <button className="cb-btn cb-btn--icon" onClick={() => setUpdateTarget(null)}><i className="fas fa-times" /></button>
            </div>
            <div className="cb-modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Mark this cheque as:</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button className="cb-btn cb-btn--primary" onClick={() => handleUpdate(updateTarget.id, 'CLEARED')}>
                  <i className="fas fa-check" /> Cleared
                </button>
                <button className="cb-btn cb-btn--danger" onClick={() => handleUpdate(updateTarget.id, 'VOID')}>
                  <i className="fas fa-ban" /> Void
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="cb-header">
        <div className="cb-header-text">
          <h1 className="cb-title"><i className="fas fa-money-check" style={{ marginRight: 8, color: 'var(--color-primary)' }} />Cheques</h1>
          <p className="cb-subtitle">Issue, track, and manage cheques by bank account</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="cb-btn cb-btn--secondary" onClick={() => setShowBook(true)}>
            <i className="fas fa-book" /> Register Book
          </button>
          <button className="cb-btn cb-btn--primary" onClick={() => setShowIssue(true)}>
            <i className="fas fa-plus" /> Issue Cheque
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }} />{error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b' }}>×</button>
        </div>
      )}

      <div className="cb-toolbar">
        <select value={filter.accountId} onChange={(e) => setFilter((f) => ({ ...f, accountId: e.target.value }))}>
          <option value="">All Accounts</option>
          {accounts.filter(a => a.isBank).map(a => (
            <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
          ))}
        </select>
        <select value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}>
          <option value="">All Statuses</option>
          <option value="ISSUED">Issued</option>
          <option value="CLEARED">Cleared</option>
          <option value="VOID">Void</option>
        </select>
      </div>

      <div className="cb-card">
        <div className="cb-card-header">
          <h2 className="cb-card-title">Cheque Register</h2>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{cheques.total} cheques</span>
        </div>
        {loading ? (
          <div style={{ padding: 32 }}><LoadingSpinner variant="default" /></div>
        ) : (
          <div className="cb-table-wrap">
            <table className="cb-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Account</th>
                  <th>Payee</th>
                  <th className="right">Amount</th>
                  <th>Issue Date</th>
                  <th>Status</th>
                  <th>Voucher</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cheques.items.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>No cheques found</td></tr>
                ) : cheques.items.map((c) => (
                  <tr key={c.id}>
                    <td className="mono">{c.chequeNumber}</td>
                    <td>{c.account?.name}</td>
                    <td>{c.payee}</td>
                    <td className="right" style={{ fontWeight: 600 }}>{fmt(c.amount)}</td>
                    <td className="mono">{c.issueDate?.slice(0, 10)}</td>
                    <td>
                      <span className={`cb-badge cb-badge--${STATUS_BADGE[c.status]}`}>
                        {STATUS_LABELS[c.status]}
                      </span>
                    </td>
                    <td className="mono" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {c.voucher ? `${c.voucher.type}-${c.voucher.voucherNumber}` : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="cb-btn cb-btn--icon" title="Print" onClick={() => navigate(`/cash-banks/cheques/print/${c.id}`)}>
                          <i className="fas fa-print" />
                        </button>
                        {c.status === 'ISSUED' && (
                          <button className="cb-btn cb-btn--icon" title="Update Status" onClick={() => setUpdateTarget(c)}>
                            <i className="fas fa-edit" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
